import {
  addPatchTestReadingRequestSchema,
  createDestructionProcedureRequestSchema,
  createPatchTestPanelRequestSchema,
  destructionProcedureEnvelopeSchema,
  destructionProcedureListEnvelopeSchema,
  dermatologyProfileEnvelopeSchema,
  patchTestPanelEnvelopeSchema,
  patchTestPanelListEnvelopeSchema,
  patchTestReadingEnvelopeSchema,
  recallStatusListEnvelopeSchema,
  recordSkinExamRequestSchema,
  skinExamEnvelopeSchema,
  updateDermatologyProfileRequestSchema,
  type AddPatchTestReadingRequest,
  type CreateDestructionProcedureRequest,
  type CreatePatchTestPanelRequest,
  type DermatologyProfileResponse,
  type DestructionProcedureResponse,
  type PatchTestPanelResponse,
  type RecallStatusResponse,
  type RecordSkinExamRequest,
  type UpdateDermatologyProfileRequest,
} from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The dermatology specialty-module API surface (`BACKLOG.md` M3), validated against `@emr/shared`'s
 * own request/response schemas — the exact same schemas `server/src/dermatology/routes.ts` validates
 * request bodies with, so this file cannot silently drift from what the server actually accepts and
 * returns. Every request/response type re-exported here comes from `@emr/shared` for the identical
 * reason `api/auth.ts` imports `authSessionResponseSchema` rather than re-deriving it.
 */

export type {
  AddPatchTestReadingRequest,
  CreateDestructionProcedureRequest,
  CreatePatchTestPanelRequest,
  DermatologyProfileResponse,
  DestructionProcedureResponse,
  PatchTestPanelResponse,
  RecallStatusResponse,
  RecordSkinExamRequest,
  UpdateDermatologyProfileRequest,
};

function derm(patientId: string, suffix = ''): string {
  return `/api/patients/${patientId}/dermatology${suffix}`;
}

export async function fetchDermatologyProfile(
  patientId: string,
  signal?: AbortSignal,
): Promise<DermatologyProfileResponse> {
  const response = await apiRequest(
    derm(patientId, '/profile'),
    dermatologyProfileEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.profile;
}

export async function updateDermatologyProfile(
  patientId: string,
  input: UpdateDermatologyProfileRequest,
): Promise<DermatologyProfileResponse> {
  updateDermatologyProfileRequestSchema.parse(input);
  const response = await apiRequest(derm(patientId, '/profile'), dermatologyProfileEnvelopeSchema, {
    method: 'PATCH',
    body: input,
  });
  return response.profile;
}

export async function fetchPatchTestPanels(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly PatchTestPanelResponse[]> {
  const response = await apiRequest(
    derm(patientId, '/patch-tests'),
    patchTestPanelListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.panels;
}

export async function createPatchTestPanel(
  patientId: string,
  input: CreatePatchTestPanelRequest,
): Promise<PatchTestPanelResponse> {
  createPatchTestPanelRequestSchema.parse(input);
  const response = await apiRequest(derm(patientId, '/patch-tests'), patchTestPanelEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.panel;
}

export async function addPatchTestReading(
  patientId: string,
  panelId: string,
  input: AddPatchTestReadingRequest,
) {
  addPatchTestReadingRequestSchema.parse(input);
  const response = await apiRequest(
    derm(patientId, `/patch-tests/${panelId}/readings`),
    patchTestReadingEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.reading;
}

export async function fetchDestructionProcedures(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly DestructionProcedureResponse[]> {
  const response = await apiRequest(
    derm(patientId, '/destruction-procedures'),
    destructionProcedureListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.procedures;
}

export async function createDestructionProcedure(
  patientId: string,
  input: CreateDestructionProcedureRequest,
): Promise<DestructionProcedureResponse> {
  createDestructionProcedureRequestSchema.parse(input);
  const response = await apiRequest(
    derm(patientId, '/destruction-procedures'),
    destructionProcedureEnvelopeSchema,
    { method: 'POST', body: input },
  );
  return response.procedure;
}

export async function fetchRecallStatuses(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly RecallStatusResponse[]> {
  const response = await apiRequest(
    derm(patientId, '/recall'),
    recallStatusListEnvelopeSchema,
    signal === undefined ? {} : { signal },
  );
  return response.statuses;
}

export async function recordSkinExam(patientId: string, input: RecordSkinExamRequest) {
  recordSkinExamRequestSchema.parse(input);
  const response = await apiRequest(derm(patientId, '/recall/skin-exams'), skinExamEnvelopeSchema, {
    method: 'POST',
    body: input,
  });
  return response.exam;
}
